import type { QueryClient } from '@tanstack/react-query';

/**
 * Stable React Query keys for org-scoped data (Dashboard, Streams).
 * Use predicate-friendly shapes: ['org', orgId, segment, ...rest].
 */

export const orgKeys = {
  all: (orgId: string) => ['org', orgId],
  stats: (orgId: string) => ['org', orgId, 'stats'],
  profile: (orgId: string) => ['org', orgId, 'profile'],
  streams: (orgId: string) => ['org', orgId, 'streams'],
  /** Projects under a stream (opts must be JSON-serialisable for stable keys). */
  streamProjects: (orgId: string, streamId: string, opts: Record<string, unknown> = { stats: false }) => [
    'org',
    orgId,
    'stream',
    streamId,
    'projects',
    opts,
  ],
  dora: (orgId: string, windowDays: number) => ['org', orgId, 'dora', windowDays],
  churn: (orgId: string, windowDays: number) => ['org', orgId, 'churn', windowDays],
  doraLabelMetrics: (orgId: string, opts: any = {}) => [
    'org',
    orgId,
    'doraLabelMetrics',
    opts.windowDays ?? 30,
    opts.granularity ?? 'day',
    opts.repositoryId ?? '',
  ],
  cycleTime: (orgId: string, windowDays: number, repositoryId: string = '') => [
    'org',
    orgId,
    'cycleTime',
    windowDays,
    repositoryId,
  ],
  codeComposition: (orgId: string, windowDays: number, scope: Record<string, string> = {}) => [
    'org',
    orgId,
    'codeComposition',
    windowDays,
    scope.repositoryId ?? '',
    scope.projectId ?? '',
    scope.streamId ?? '',
  ],
  codeCompositionByPeriod: (
    orgId: string,
    windowDays: number,
    period: string,
    scope: Record<string, string> = {}
  ) => [
    'org',
    orgId,
    'codeCompositionByPeriod',
    windowDays,
    period,
    scope.repositoryId ?? '',
    scope.projectId ?? '',
    scope.streamId ?? '',
  ],
  contributorAnalytics: (orgId: string, timeframe: string, status: string) => [
    'org',
    orgId,
    'contributorAnalytics',
    timeframe,
    status,
  ],
  vantiInsights: (orgId: string) => ['org', orgId, 'vantiInsights'],
  repositories: (orgId: string, search: string = '') => ['org', orgId, 'repositories', search || ''],
  commits: (orgId: string, params: Record<string, unknown> = {}) => [
    'org',
    orgId,
    'commits',
    params.limit ?? 50,
    params.offset ?? 0,
    params.branch ?? '',
    params.author ?? '',
    params.from ?? '',
    params.to ?? '',
    params.message ?? '',
    params.repositoryId ?? '',
    params.sortBy ?? 'authorDate',
    params.sortDir ?? 'desc',
  ],
  pullRequests: (orgId: string, params: Record<string, unknown> = {}) => [
    'org',
    orgId,
    'pullRequests',
    params.limit ?? 50,
    params.offset ?? 0,
    params.state ?? 'all',
    params.repositoryId ?? '',
    params.search ?? '',
    params.sortBy ?? 'createdAt',
    params.sortDir ?? 'desc',
  ],
  discoveredRepositories: (orgId: string, search: string = '') => [
    'org',
    orgId,
    'discoveredRepositories',
    search || '',
  ],
  members: (orgId: string, params: any = {}) => [
    'org',
    orgId,
    'members',
    params.search ?? '',
    params.streamId ?? '',
    params.projectId ?? '',
    params.repositoryId ?? '',
    params.stats ?? false,
  ],
  invites: (orgId: string) => ['org', orgId, 'invites'],
  accessRequests: (orgId: string) => ['org', orgId, 'accessRequests'],
  streamsDropdown: (orgId: string) => ['org', orgId, 'streamsDropdown'],
  /** Batched dashboard metrics; payload must be a plain object with stable field order for keys. */
  dashboardMetrics: (orgId: string, payload: Record<string, unknown>) => [
    'org',
    orgId,
    'dashboardMetrics',
    payload,
  ],
  githubTokens: (orgId: string) => ['org', orgId, 'githubTokens'],
  aiProviders: (orgId: string) => ['org', orgId, 'aiProviders'],
  aiConfig: (orgId: string) => ['org', orgId, 'aiConfig'],
  aiUsage: (orgId: string, days: number, feature?: string) => [
    'org',
    orgId,
    'aiUsage',
    days,
    feature ?? '',
  ],
  cursorLinks: (orgId: string) => ['org', orgId, 'cursorLinks'],
  cursorUsage: (orgId: string, params: Record<string, unknown> = {}) => [
    'org',
    orgId,
    'cursorUsage',
    params,
  ],
  aiLeverage: (orgId: string, params: Record<string, unknown> = {}) => [
    'org',
    orgId,
    'aiLeverage',
    params,
  ],
  assetRegister: (orgId: string) => ['org', orgId, 'assetRegister'],
  assetRegisterAdminStatus: (orgId: string) => ['org', orgId, 'assetRegister', 'admin-status'],
  leaveApplicationsConfig: (orgId: string) => ['org', orgId, 'leaveApplications', 'config'],
  leaveApplicationsAdminStatus: (orgId: string) => ['org', orgId, 'leaveApplications', 'admin-status'],
  leaveApplicationsNotificationRecipients: (orgId: string) => [
    'org',
    orgId,
    'leaveApplications',
    'notification-recipients',
  ],
  leaveApplicationsMine: (orgId: string, tab: string) => ['org', orgId, 'leaveApplications', 'mine', tab],
  leaveApplicationsAdminOverview: (orgId: string) => ['org', orgId, 'leaveApplications', 'admin', 'overview'],
  leaveApplicationsAdminList: (orgId: string, mode: string) => [
    'org',
    orgId,
    'leaveApplications',
    'admin',
    'list',
    mode,
  ],
  leaveApplicationsAdminMembers: (orgId: string, year?: number) => [
    'org',
    orgId,
    'leaveApplications',
    'admin',
    'members',
    year ?? 'current',
  ],
  leaveApplicationsAdminMember: (orgId: string, userId: string, year: string = 'all') => [
    'org',
    orgId,
    'leaveApplications',
    'admin',
    'member',
    userId,
    year,
  ],
  assets: (orgId: string, opts: Record<string, unknown> = {}) => [
    'org',
    orgId,
    'assets',
    opts.search ?? '',
    opts.status ?? '',
    opts.assetType ?? '',
    opts.sortBy ?? 'assetType',
    opts.sortDir ?? 'asc',
  ],
  asset: (orgId: string, assetId: string) => ['org', orgId, 'assets', assetId],
  assetTransfers: (orgId: string, assetId: string) => ['org', orgId, 'assets', assetId, 'transfers'],
  assetStatusChanges: (orgId: string, assetId: string) =>
    ['org', orgId, 'assets', assetId, 'status-changes'],
  assetEdits: (orgId: string, assetId: string) => ['org', orgId, 'assets', assetId, 'edits'],
  myAsset: (orgId: string, assetId: string) => ['org', orgId, 'assets', 'mine', assetId],
  assetReportsByEmployee: (orgId: string) => ['org', orgId, 'assets', 'reports', 'by-employee'],
  myAssets: (orgId: string, opts: Record<string, unknown> = {}) => [
    'org',
    orgId,
    'assets',
    'mine',
    opts.search ?? '',
    opts.sortBy ?? 'name',
    opts.sortDir ?? 'asc',
  ],
  assetReportsSummary: (orgId: string) => ['org', orgId, 'assets', 'reports', 'summary'],
  assetMembers: (orgId: string, opts: Record<string, unknown> = {}) => [
    'org',
    orgId,
    'assets',
    'members',
    opts.search ?? '',
    opts.sortBy ?? 'username',
    opts.sortDir ?? 'asc',
  ],
  assetMemberHistory: (orgId: string, userId: string) => [
    'org',
    orgId,
    'assets',
    'members',
    userId,
  ],
};

export const reviewCyclesKeys = {
  all: (orgId: string) => ['reviewCycles', orgId],
  list: (orgId: string) => ['reviewCycles', orgId, 'list'],
};

export const notificationKeys = {
  list: (params: Record<string, unknown>) => ['notifications', 'list', params],
  unreadCount: () => ['notifications', 'unreadCount'],
  unreadBadgeList: () => ['notifications', 'unreadBadgeList'],
};

export const attendanceRegisterKeys = {
  list: (orgId: string, params: any = {}) => [
    'attendanceRegister',
    orgId,
    'list',
    params.sort ?? 'startsAt_desc',
    params.status ?? '',
    params.organiserId ?? '',
    params.startsFrom ?? '',
    params.startsTo ?? '',
    params.search ?? '',
  ],
  detail: (orgId: string, registerId: string) => ['attendanceRegister', orgId, registerId],
};

export function invalidateAttendanceRegisterList(queryClient: QueryClient, orgId: string) {
  if (!orgId) return;
  queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      return Array.isArray(k) && k[0] === 'attendanceRegister' && k[1] === orgId && k[2] === 'list';
    },
  });
}

export const sesKeys = {
  all: (orgId: string) => ['ses', orgId],
  sessions: (orgId: string, params: any = {}) => [
    'ses',
    orgId,
    'sessions',
    params.search ?? '',
    params.status ?? '',
  ],
  session: (orgId: string, sesId: string) => ['ses', orgId, 'session', sesId],
  cycles: (orgId: string, sesId: string) => ['ses', orgId, 'session', sesId, 'cycles'],
};

export function invalidateSesCaches(queryClient: QueryClient, orgId: string, sesId?: string) {
  if (!orgId) return;
  queryClient.invalidateQueries({ queryKey: sesKeys.all(orgId) });
  if (sesId) {
    queryClient.invalidateQueries({ queryKey: sesKeys.session(orgId, sesId) });
    queryClient.invalidateQueries({ queryKey: sesKeys.cycles(orgId, sesId) });
  }
}

export const surveyKeys = {
  detail: (orgId: string, surveyId: string) => ['survey', orgId, surveyId],
  sets: (orgId: string) => ['survey', orgId, 'sets'],
  importJob: (orgId: string, jobId: string) => ['survey', orgId, 'importJob', jobId],
};

export function invalidateSurveyDetail(queryClient: QueryClient, orgId: string, surveyId: string) {
  if (!orgId || !surveyId) return;
  queryClient.invalidateQueries({ queryKey: surveyKeys.detail(orgId, surveyId) });
}

export const configurationKeys = {
  tree: (orgId: string) => ['configuration', orgId, 'tree'],
};

export function invalidateConfigurationTree(queryClient: QueryClient, orgId: string) {
  if (!orgId) return;
  queryClient.invalidateQueries({ queryKey: configurationKeys.tree(orgId) });
  queryClient.invalidateQueries({ queryKey: orgKeys.streams(orgId) });
}

export const projectPageKeys = {
  detail: (orgId: string, streamId: string, projectId: string) => [
    'projectPage',
    orgId,
    streamId,
    projectId,
    'detail',
  ],
  repos: (orgId: string, streamId: string, projectId: string) => [
    'projectPage',
    orgId,
    streamId,
    projectId,
    'repos',
  ],
  dashboard: (orgId: string, streamId: string, projectId: string) => [
    'projectPage',
    orgId,
    streamId,
    projectId,
    'dashboard',
  ],
  members: (orgId: string, streamId: string, projectId: string) => [
    'projectPage',
    orgId,
    streamId,
    projectId,
    'members',
  ],
  dora: (orgId: string, streamId: string, projectId: string, windowDays: number) => [
    'projectPage',
    orgId,
    streamId,
    projectId,
    'dora',
    windowDays,
  ],
  churn: (orgId: string, streamId: string, projectId: string, windowDays: number) => [
    'projectPage',
    orgId,
    streamId,
    projectId,
    'churn',
    windowDays,
  ],
  cycleTime: (orgId: string, streamId: string, projectId: string, windowDays: number) => [
    'projectPage',
    orgId,
    streamId,
    projectId,
    'cycleTime',
    windowDays,
  ],
  codeComposition: (orgId: string, streamId: string, projectId: string, windowDays: number) => [
    'projectPage',
    orgId,
    streamId,
    projectId,
    'codeComposition',
    windowDays,
  ],
  doraLabelMetrics: (orgId: string, streamId: string, projectId: string, opts: any = {}) => [
    'projectPage',
    orgId,
    streamId,
    projectId,
    'doraLabelMetrics',
    opts.windowDays ?? 30,
  ],
  contributorAnalytics: (
    orgId: string,
    streamId: string,
    projectId: string,
    timeframe: string,
    status: string
  ) => [
    'projectPage',
    orgId,
    streamId,
    projectId,
    'contributorAnalytics',
    timeframe,
    status,
  ],
};

export function invalidateProjectPageCaches(
  queryClient: QueryClient,
  orgId: string,
  streamId: string,
  projectId: string
) {
  if (!orgId || !streamId || !projectId) return;
  queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      return (
        Array.isArray(k) &&
        k[0] === 'projectPage' &&
        k[1] === orgId &&
        k[2] === streamId &&
        k[3] === projectId
      );
    },
  });
}

export const surveySessionKeys = {
  summary: (orgId: string, sessionId: string) => ['surveySession', orgId, sessionId, 'summary'],
  participants: (orgId: string, sessionId: string) => [
    'surveySession',
    orgId,
    sessionId,
    'participants',
  ],
};

export function invalidateAttendanceRegister(
  queryClient: QueryClient,
  orgId: string,
  registerId: string
) {
  if (!orgId || !registerId) return;
  queryClient.invalidateQueries({ queryKey: attendanceRegisterKeys.detail(orgId, registerId) });
}

export function invalidateSurveySession(queryClient: QueryClient, orgId: string, sessionId: string) {
  if (!orgId || !sessionId) return;
  queryClient.invalidateQueries({ queryKey: ['surveySession', orgId, sessionId] });
}

export const commitActivityKeys = {
  activity: (params: Record<string, unknown>) => [
    'commitActivity',
    params.scope,
    params.organisationId ?? '',
    params.year,
    params.metric,
    params.streamId ?? '',
    params.projectId ?? '',
    params.repositoryId ?? '',
    params.memberId ?? '',
    params.memberIds ?? '',
  ],
};

export const streamPageKeys = {
  /** Stream detail page: metrics for one stream (orgId + streamId). */
  dora: (orgId: string, streamId: string, windowDays: number) => [
    'streamPage',
    orgId,
    streamId,
    'dora',
    windowDays,
  ],
  churn: (orgId: string, streamId: string, windowDays: number) => [
    'streamPage',
    orgId,
    streamId,
    'churn',
    windowDays,
  ],
  cycleTime: (orgId: string, streamId: string, windowDays: number) => [
    'streamPage',
    orgId,
    streamId,
    'cycleTime',
    windowDays,
  ],
  codeComposition: (orgId: string, streamId: string, windowDays: number) => [
    'streamPage',
    orgId,
    streamId,
    'codeComposition',
    windowDays,
  ],
  projects: (orgId: string, streamId: string) => ['streamPage', orgId, streamId, 'projects'],
  members: (orgId: string, streamId: string) => ['streamPage', orgId, streamId, 'members'],
  doraLabelMetrics: (orgId: string, streamId: string, opts: any = {}) => [
    'streamPage',
    orgId,
    streamId,
    'doraLabelMetrics',
    opts.windowDays ?? 30,
    opts.granularity ?? 'day',
    opts.repositoryId ?? '',
  ],
  contributorAnalytics: (orgId: string, streamId: string, timeframe: string, status: string) => [
    'streamPage',
    orgId,
    streamId,
    'contributorAnalytics',
    timeframe,
    status,
  ],
};

/** After stream/project changes: refresh org stream list and stream detail caches. */
export function invalidateStreamListAndPage(
  queryClient: QueryClient,
  orgId: string,
  streamId?: string
) {
  if (!orgId) return;
  queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      if (!Array.isArray(k)) return false;
      if (k[0] === 'org' && k[1] === orgId && k[2] === 'streams') return true;
      if (k[0] === 'streamPage' && k[1] === orgId && (streamId == null || k[2] === streamId))
        return true;
      return false;
    },
  });
}

export const repoKeys = {
  byId: (repositoryId: string) => ['repository', repositoryId, 'detail'],
  commits: (repositoryId: string, params: any = {}) => [
    'repository',
    repositoryId,
    'commits',
    params,
  ],
  activity: (repositoryId: string) => ['repository', repositoryId, 'activity'],
  pullRequests: (repositoryId: string) => ['repository', repositoryId, 'pullRequests'],
  contributors: (repositoryId: string) => ['repository', repositoryId, 'contributors'],
  contributorsCard: (repositoryId: string, timeframe: string, status: string) => [
    'repository',
    repositoryId,
    'contributorsCard',
    timeframe,
    status,
  ],
  branches: (repositoryId: string, opts: any = {}) => ['repository', repositoryId, 'branches', opts],
  techStack: (repositoryId: string) => ['repository', repositoryId, 'techStack'],
  dora: (repositoryId: string, windowDays: number) => ['repository', repositoryId, 'dora', windowDays],
  churn: (repositoryId: string, windowDays: number) => ['repository', repositoryId, 'churn', windowDays],
  cycleTime: (repositoryId: string, windowDays: number) => [
    'repository',
    repositoryId,
    'cycleTime',
    windowDays,
  ],
  codeComposition: (repositoryId: string, windowDays: number) => [
    'repository',
    repositoryId,
    'codeComposition',
    windowDays,
  ],
  doraLabelMetrics: (repositoryId: string, opts: any = {}) => [
    'repository',
    repositoryId,
    'doraLabelMetrics',
    opts.windowDays ?? 30,
  ],
};

/**
 * After stream/project mutations: refresh counts, stream list, per-stream projects, repos list, batch metrics.
 */
export function invalidateOrgStreamsProjectCaches(queryClient: QueryClient, orgId: string) {
  if (!orgId) return;
  queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      if (!Array.isArray(k) || k[0] !== 'org' || k[1] !== orgId) return false;
      if (
        k[2] === 'stats' ||
        k[2] === 'streams' ||
        k[2] === 'repositories' ||
        k[2] === 'dashboardMetrics'
      )
        return true;
      if (k[2] === 'discoveredRepositories') return true;
      if (k[2] === 'stream' && k[4] === 'projects') return true;
      return false;
    },
  });
}

/** After member/invite/request mutations: refresh members list and org stats. */
export function invalidateOrgMembersCaches(queryClient: QueryClient, orgId: string) {
  if (!orgId) return;
  queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      if (!Array.isArray(k) || k[0] !== 'org' || k[1] !== orgId) return false;
      return (
        k[2] === 'members' || k[2] === 'invites' || k[2] === 'accessRequests' || k[2] === 'stats'
      );
    },
  });
}

/** After repository connect/refresh: refresh profile, connected, and discovered repo caches. */
export function invalidateOrgRepositoryCaches(queryClient: QueryClient, orgId: string) {
  if (!orgId) return;
  queryClient.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      if (!Array.isArray(k) || k[0] !== 'org' || k[1] !== orgId) return false;
      return (
        k[2] === 'profile' ||
        k[2] === 'repositories' ||
        k[2] === 'discoveredRepositories' ||
        k[2] === 'stats'
      );
    },
  });
}
